import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';

import { StoreCode } from 'schema/storeCode';
import { ISOLocalesEnum } from 'schema/locale';
import { MONITORING_TAGS } from 'constants/monitoring';
import { apiRoutes } from 'constants/routes';
import { useLocale } from 'shared/hooks/useLocale';
import { useSendDataDogCustomEvent } from 'shared/hooks/useSendDataDogCustomEvent';
import runInBackground from 'shared/utils/runInBackground';
import { fetcher } from 'shared/utils/fetcher';
import { getISOLocale } from 'shared/utils/locale';
import { CheckoutResponse, ProductCheckoutItem } from 'state/upscribe/getCheckout/types';

export type KlaviyoServiceArgs = {
  store: StoreCode;
  language: ISOLocalesEnum;
  email: string;
  updatedListId?: string;
};

export async function klaviyoConnect({
  store,
  language,
  email,
  updatedListId
}: KlaviyoServiceArgs) {
  const { data } = await fetcher.post(apiRoutes.klaviyo.subscribe({ store, language }), {
    email,
    updatedListId
  });

  runInBackground(
    () =>
      fetcher.post(apiRoutes.klaviyo.emailSignUp({ store }), {
        email
      }),
    { user: { email }, feature: 'facebook-email-signup' }
  );

  return data;
}

export function useMutationKlaviyo() {
  return useMutation({
    mutationFn: (variables: KlaviyoServiceArgs) => klaviyoConnect(variables)
  });
}

const normalizeKlaviyoItem = (itemData: ProductCheckoutItem) => ({
  ImageURL: itemData.image_url,
  ItemPrice: itemData.price,
  ProductCategories: [itemData.variant_title],
  ProductID: itemData.product_id,
  ProductName: itemData.title,
  ProductURL: 'https://drinkag1.com#offers',
  Quantity: itemData.quantity,
  RowTotal: itemData.price,
  SKU: itemData.sku
});

// Klaviyo Started Checkout / Abandoned Cart
export function useSendCheckoutKlaviyoIdentifyTrack() {
  const locale = useLocale();
  const sendDataDogCustomEvent = useSendDataDogCustomEvent();

  return (data: CheckoutResponse, url: string) => {
    try {
      const { klaviyo, next } = window;
      if (!klaviyo) {
        throw new Error('No Klaviyo object available');
      }
      const language = next?.ISOLocale || getISOLocale(locale);

      const identifyProperties = {
        email: data.customer_data.email,
        first_name: data.customer_data.first_name,
        last_name: data.customer_data.last_name,
        language
      };
      const items = data.line_items;
      const trackProperties = {
        $eventId: `${data.token}_${Math.floor(Date.now() / 1000)}`,
        $value: data.total_price,
        Categories: items.map((i) => i.variant_title),
        CheckoutURL: url,
        ItemNames: items.map((i) => i.title),
        Items: items.map((i) => normalizeKlaviyoItem(i)),
        language
      };

      const trackSuccessCallback = () =>
        sendDataDogCustomEvent({
          eventType: 'klaviyoStartedCheckout',
          data: {
            identify: identifyProperties,
            track: trackProperties
          },
          success: true
        });

      const identifyCallback = () =>
        klaviyo.track('Started Checkout', trackProperties, trackSuccessCallback);

      klaviyo.identify(identifyProperties, identifyCallback);
    } catch (error) {
      const tags = {
        [MONITORING_TAGS.ANALYTICS]: true,
        [MONITORING_TAGS.CHECKOUT]: true
      };
      datadogRum.addError(error, { tags });
    }
  };
}
